import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useLanguage from '../../../hooks/useLanguage';
import '../DesktopPostReviewPage/DesktopPostReviewPage.css';
import './DesktopCampaignSelector.css';
import DesktopHorizontalScroll from '../common/scroll/DesktopHorizontalScroll';

const normalizeId = (value) => {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string' || typeof value === 'number') return String(value);

  if (typeof value === 'object') {
    if (value.$oid) return String(value.$oid);
    if (value.id || value._id) return normalizeId(value.id ?? value._id);
    if (typeof value.toString === 'function') {
      const stringified = value.toString();
      if (stringified && stringified !== '[object Object]') {
        return String(stringified);
      }
    }
  }

  return null;
};

const DesktopCampaignSelector = ({
  campaigns = [],
  selectionMode = 'multiple',
  selectedCampaignIds = [],
  onCampaignSelectionChange,
  activeCampaign = null,
  onCampaignSelect,
  requireConfirmation = true,
  className = '',
  title,
  eyebrow,
}) => {
  const { t, language } = useLanguage();
  const [localSelectedIds, setLocalSelectedIds] = useState(
    () => selectedCampaignIds.map(normalizeId).filter(Boolean)
  );
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  useEffect(() => {
    if (selectionMode === 'single') {
      const activeId = normalizeId(activeCampaign?.id ?? activeCampaign?._id ?? activeCampaign?.value);
      if (activeId) {
        if (localSelectedIds.length !== 1 || localSelectedIds[0] !== activeId) {
          setLocalSelectedIds([activeId]);
        }
      } else if (localSelectedIds.length > 0) {
        setLocalSelectedIds([]);
      }
      return;
    }

    const normalized = selectedCampaignIds.map(normalizeId).filter(Boolean);
    const isDifferent = normalized.length !== localSelectedIds.length ||
      normalized.some((id, index) => id !== localSelectedIds[index]);

    if (isDifferent) {
      setLocalSelectedIds(normalized);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionMode, selectedCampaignIds, activeCampaign?.id]);

  const locale = useMemo(() => {
    if (language === 'en') return 'en-US';
    if (language === 'it') return 'it-IT';
    return language || 'it-IT';
  }, [language]);

  const preparedCampaigns = useMemo(() => {
    const byId = new Map();
    campaigns.forEach((campaign) => {
      if (!campaign) return;
      const id = normalizeId(campaign.id) || normalizeId(campaign._id);
      if (!id) return;
      if (!byId.has(id)) {
        byId.set(id, { ...campaign, id });
      }
    });
    return Array.from(byId.values());
  }, [campaigns]);

  const periodOptions = useMemo(() => ([
    { value: 'all', label: t('campaignSelector.filters.period.all', { defaultValue: 'Tutti i periodi' }) },
    { value: 'last30', label: t('campaignSelector.filters.period.last30', { defaultValue: 'Ultimi 30 giorni' }) },
    { value: 'last90', label: t('campaignSelector.filters.period.last90', { defaultValue: 'Ultimi 90 giorni' }) }
  ]), [t]);

  const statusOptions = useMemo(() => ([
    { value: 'all', label: t('campaignSelector.filters.status.all', { defaultValue: 'Tutti gli stati' }) },
    { value: 'draft', label: t('campaignSelector.filters.status.draft', { defaultValue: 'Bozza' }) },
    { value: 'active', label: t('campaignSelector.filters.status.active', { defaultValue: 'Attiva' }) },
    { value: 'paused', label: t('campaignSelector.filters.status.paused', { defaultValue: 'In pausa' }) },
    { value: 'completed', label: t('campaignSelector.filters.status.completed', { defaultValue: 'Completata' }) }
  ]), [t]);

  const filteredCampaigns = useMemo(() => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
    const ninetyDaysAgo = new Date(now.getTime() - (90 * 24 * 60 * 60 * 1000));

    return preparedCampaigns.filter((campaign) => {
      let matchesPeriod = true;
      if (selectedPeriod !== 'all') {
        const startDateValue = campaign.start_date || campaign.startDate;
        const dateString = (typeof startDateValue === 'object' && startDateValue?.$date)
          ? startDateValue.$date
          : startDateValue;
        const startDate = new Date(dateString);

        if (selectedPeriod === 'last30') {
          matchesPeriod = startDate >= thirtyDaysAgo;
        } else if (selectedPeriod === 'last90') {
          matchesPeriod = startDate >= ninetyDaysAgo;
        }
      }

      if (!matchesPeriod) return false;
      if (selectedStatus === 'all') return true;

      const status = (campaign.status || '').toString().toLowerCase();
      return status === selectedStatus;
    });
  }, [preparedCampaigns, selectedPeriod, selectedStatus]);

  const selectedCount = localSelectedIds.length;
  const isAllSelected = selectionMode === 'multiple' &&
    filteredCampaigns.length > 0 &&
    selectedCount === filteredCampaigns.length;
  const hasActionButtons = selectionMode === 'multiple' || requireConfirmation;

  const applyButtonLabel = useMemo(() => {
    if (selectionMode === 'single') {
      return selectedCount === 0
        ? t('campaignSelector.actions.selectCampaign', { defaultValue: 'Seleziona campagna' })
        : t('campaignSelector.actions.apply', { defaultValue: 'Applica' });
    }

    if (selectedCount === 0) {
      return t('campaignSelector.actions.applyAll', { defaultValue: 'Applica a tutte' });
    }

    return t('campaignSelector.actions.applyCount', { count: selectedCount, defaultValue: `Applica ${selectedCount}` });
  }, [selectionMode, selectedCount, t]);

  const statusLabelMap = useMemo(() => ({
    draft: t('campaignSelector.status.draft', { defaultValue: 'Bozza' }),
    active: t('campaignSelector.status.active', { defaultValue: 'Attiva' }),
    paused: t('campaignSelector.status.paused', { defaultValue: 'In pausa' }),
    completed: t('campaignSelector.status.completed', { defaultValue: 'Completata' })
  }), [t]);

  const getStatusLabel = useCallback((status) => statusLabelMap[status] || status || '', [statusLabelMap]);

  const startDateFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short' });
    } catch (error) {
      return new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: 'short' });
    }
  }, [locale]);

  const endDateFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (error) {
      return new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
    }
  }, [locale]);

  const formatCampaignDateRange = useCallback((startDateValue, endDateValue) => {
    const parseDate = (value) => {
      if (!value) return null;
      const dateString = typeof value === 'object' && value.$date ? value.$date : value;
      const parsed = new Date(dateString);
      if (Number.isNaN(parsed.getTime())) return null;
      if (typeof dateString === 'string' && dateString.endsWith('Z')) {
        const year = parsed.getUTCFullYear();
        const month = parsed.getUTCMonth();
        const day = parsed.getUTCDate();
        const hours = parsed.getUTCHours();
        const minutes = parsed.getUTCMinutes();
        const seconds = parsed.getUTCSeconds();
        return new Date(year, month, day, hours, minutes, seconds);
      }
      return parsed;
    };

    const startDate = parseDate(startDateValue);
    const endDate = parseDate(endDateValue);

    if (!startDate || !endDate) {
      return t('campaignSelector.cards.datesUnavailable', { defaultValue: 'Date non disponibili' });
    }

    try {
      const formattedStart = startDateFormatter.format(startDate);
      const formattedEnd = endDateFormatter.format(endDate);
      return t('campaignSelector.cards.dateRange', {
        start: formattedStart,
        end: formattedEnd,
        defaultValue: `${formattedStart} - ${formattedEnd}`
      });
    } catch (error) {
      return t('campaignSelector.cards.datesUnavailable', { defaultValue: 'Date non disponibili' });
    }
  }, [endDateFormatter, startDateFormatter, t]);

  const notifySelection = useCallback((ids, selectionType = 'specific') => {
    if (selectionMode === 'multiple' && onCampaignSelectionChange) {
      onCampaignSelectionChange(ids, selectionType);
    }

    if (selectionMode === 'single' && onCampaignSelect) {
      const selectedCampaign = preparedCampaigns.find((campaign) => campaign.id === ids[0]);
      if (selectedCampaign) {
        onCampaignSelect(selectedCampaign);
      }
    }
  }, [selectionMode, onCampaignSelectionChange, onCampaignSelect, preparedCampaigns]);

  const handleCampaignClick = useCallback((campaign) => {
    const id = campaign?.id;
    if (!id) return;

    if (selectionMode === 'single') {
      const nextSelection = [id];
      setLocalSelectedIds(nextSelection);
      if (!requireConfirmation) {
        notifySelection(nextSelection, 'specific');
      }
      return;
    }

    const exists = localSelectedIds.includes(id);
    const nextSelection = exists
      ? localSelectedIds.filter((cid) => cid !== id)
      : [...localSelectedIds, id];

    setLocalSelectedIds(nextSelection);
    if (!requireConfirmation) {
      notifySelection(nextSelection, 'specific');
    }
  }, [localSelectedIds, notifySelection, requireConfirmation, selectionMode]);

  const handleAllCampaignsClick = useCallback(() => {
    if (selectionMode === 'single') return;
    const nextSelection = isAllSelected ? [] : filteredCampaigns.map((campaign) => campaign.id);
    setLocalSelectedIds(nextSelection);

    if (!requireConfirmation && onCampaignSelectionChange) {
      const selectionType = isAllSelected ? 'clear' : 'all';
      onCampaignSelectionChange(nextSelection, selectionType);
    }
  }, [filteredCampaigns, isAllSelected, onCampaignSelectionChange, requireConfirmation, selectionMode]);

  const handleClearAll = useCallback(() => {
    if (selectionMode === 'single') return;
    setLocalSelectedIds([]);
    if (!requireConfirmation && onCampaignSelectionChange) {
      onCampaignSelectionChange([], 'clear');
    }
  }, [onCampaignSelectionChange, requireConfirmation, selectionMode]);

  const handlePeriodChange = useCallback((period) => {
    setSelectedPeriod(period);
    if (!requireConfirmation && selectionMode === 'multiple') {
      setLocalSelectedIds([]);
      if (onCampaignSelectionChange) {
        onCampaignSelectionChange([], 'period_change');
      }
    }
  }, [onCampaignSelectionChange, requireConfirmation, selectionMode]);

  const handleStatusChange = useCallback((status) => {
    setSelectedStatus(status);
    if (!requireConfirmation && selectionMode === 'multiple') {
      setLocalSelectedIds([]);
      if (onCampaignSelectionChange) {
        onCampaignSelectionChange([], 'status_change');
      }
    }
  }, [onCampaignSelectionChange, requireConfirmation, selectionMode]);

  const handleApply = useCallback(() => {
    if (selectionMode === 'multiple') {
      const selectionType = localSelectedIds.length === 0 ||
        (localSelectedIds.length === filteredCampaigns.length && filteredCampaigns.length > 0)
        ? 'all'
        : 'specific';
      if (onCampaignSelectionChange) {
        onCampaignSelectionChange(localSelectedIds, selectionType);
      }
    } else if (selectionMode === 'single') {
      const selectedCampaign = preparedCampaigns.find((c) => c.id === localSelectedIds[0]);
      if (selectedCampaign && onCampaignSelect) {
        onCampaignSelect(selectedCampaign);
      }
    }
  }, [filteredCampaigns, localSelectedIds, onCampaignSelect, onCampaignSelectionChange, preparedCampaigns, selectionMode]);

  const resolvedTitle = title || t('campaignSelector.header.title', { defaultValue: 'Campagne' });
  const resolvedEyebrow = eyebrow || t('campaignSelector.desktop.eyebrow', { defaultValue: 'Pianificazione' });

  if (preparedCampaigns.length === 0) {
    return (
      <div className={`loopino-desktop-post-strip loopino-desktop-campaign-selector ${className}`.trim()}>
        <div className="loopino-desktop-post-strip-header">
          <div>
            <p className="loopino-desktop-post-strip-eyebrow">{resolvedEyebrow}</p>
            <h3 className="loopino-desktop-post-strip-title">{resolvedTitle}</h3>
          </div>
        </div>
        <div className="loopino-desktop-campaign-empty">
          {t('campaignSelector.empty.message', { defaultValue: 'Nessuna campagna disponibile.' })}
        </div>
      </div>
    );
  }

  return (
    <div className={`loopino-desktop-post-strip loopino-desktop-campaign-selector ${className}`.trim()}>
      <div className="loopino-desktop-post-strip-header">
        <div>
          <p className="loopino-desktop-post-strip-eyebrow">{resolvedEyebrow}</p>
          <h3 className="loopino-desktop-post-strip-title">{resolvedTitle}</h3>
        </div>
        <div className="loopino-desktop-campaign-toolbar">
          <div className="loopino-desktop-campaign-filters">
            <div className="loopino-desktop-campaign-filter">
              <div className="loopino-desktop-campaign-filter-chips">
                {periodOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`loopino-desktop-campaign-chip ${selectedPeriod === option.value ? 'is-active' : ''}`}
                    onClick={() => handlePeriodChange(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="loopino-desktop-campaign-filter">
              <div className="loopino-desktop-campaign-filter-chips">
                {statusOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`loopino-desktop-campaign-chip ${selectedStatus === option.value ? 'is-active' : ''}`}
                    onClick={() => handleStatusChange(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {hasActionButtons && (
            <div className="loopino-desktop-campaign-actions">
              {selectionMode === 'multiple' && (
                <button
                  type="button"
                  className={`loopino-desktop-campaign-ghost ${isAllSelected ? 'is-active' : ''}`}
                  onClick={handleAllCampaignsClick}
                >
                  {t('campaignSelector.actions.all', { defaultValue: 'Tutte' })}
                </button>
              )}
              {selectedCount > 0 && selectionMode === 'multiple' && (
                <button type="button" className="loopino-desktop-campaign-ghost" onClick={handleClearAll}>
                  {t('campaignSelector.actions.clear', { defaultValue: 'Azzera' })}
                </button>
              )}
              {requireConfirmation && (
                <button type="button" className="loopino-desktop-campaign-primary" onClick={handleApply}>
                  {applyButtonLabel}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <DesktopHorizontalScroll
        trackClassName="loopino-desktop-post-strip-track loopino-desktop-campaign-track"
        padding="20px"
      >
        {filteredCampaigns.map((campaign) => {
          const status = (campaign.status || 'draft').toString().toLowerCase();
          const isSelected = localSelectedIds.includes(campaign.id);

          return (
            <button
              key={campaign.id}
              type="button"
              className={[
                'loopino-desktop-campaign-card',
                `status-${status}`,
                isSelected ? 'is-selected' : ''
              ].join(' ')}
              onClick={() => handleCampaignClick(campaign)}
              aria-pressed={isSelected}
            >
              <div className="loopino-desktop-campaign-card-header">
                <span className="loopino-desktop-campaign-status">
                  {getStatusLabel(status) || t('campaignSelector.status.draft', { defaultValue: 'Bozza' })}
                </span>
                <span className="loopino-desktop-campaign-check" aria-hidden="true">
                  {isSelected ? '✔' : ''}
                </span>
              </div>
              <div className="loopino-desktop-campaign-card-body">
                <h4 className="loopino-desktop-campaign-name">
                  {campaign.name || campaign.title || t('campaignSelector.cards.untitled', { defaultValue: 'Campagna senza titolo' })}
                </h4>
                <p className="loopino-desktop-campaign-dates">
                  {formatCampaignDateRange(campaign.start_date || campaign.startDate, campaign.end_date || campaign.endDate)}
                </p>
              </div>
            </button>
          );
        })}

        {filteredCampaigns.length === 0 && (
          <div className="loopino-desktop-campaign-empty">
            {t('campaignSelector.empty.message', { defaultValue: 'Nessuna campagna corrisponde ai filtri selezionati.' })}
          </div>
        )}
      </DesktopHorizontalScroll>

    </div>
  );
};

export default DesktopCampaignSelector;
